import React, { useState } from 'react';
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Info, ArrowLeft } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

// En-têtes du modèle (ordre = mapping côté backend, positionnel)
const ENTETES = [
  'Catégorie', 'Nom du produit', 'Description',
  'Variation 1', 'Variation 2',
  'Prix magasin', 'Prix livraison', 'Stock initial', "Prix d'achat",
];

export default function ImportProduits({ categories = [], onSuccess, onCancel }) {
  const [etape, setEtape] = useState('accueil');   // accueil | apercu | rapport
  const [lignes, setLignes] = useState([]);
  const [nomFichier, setNomFichier] = useState('');
  const [chargement, setChargement] = useState(false);
  const [rapport, setRapport] = useState(null);

  const caracsDe = (c) => (c.attributs || []).map(a => a.nom);

  // ── Génération du modèle Excel (dynamique selon les catégories) ────────────
  const telechargerModele = async () => {
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();

      // Feuille « Produits » : en-têtes seuls (pas de ligne d'exemple à importer)
      const wsP = XLSX.utils.aoa_to_sheet([ENTETES]);
      wsP['!cols'] = [
        { wch: 18 }, { wch: 26 }, { wch: 28 }, { wch: 16 }, { wch: 16 },
        { wch: 13 }, { wch: 13 }, { wch: 12 }, { wch: 13 },
      ];
      XLSX.utils.book_append_sheet(wb, wsP, 'Produits');

      // Feuille « Instructions »
      const info = [
        ['MODÈLE D\'IMPORT — GESTION COMMERCE'],
        [],
        ['Comment remplir la feuille « Produits » :'],
        ['• Une LIGNE = une VARIANTE. Pour décliner un produit, répétez la même Catégorie et le même Nom sur plusieurs lignes.'],
        ['• « Variation 1 » et « Variation 2 » = les valeurs des caractéristiques de la catégorie (voir le tableau ci-dessous).'],
        ['• Laissez « Variation 2 » vide si la catégorie n\'a qu\'une seule caractéristique ; laissez les deux vides pour un produit simple.'],
        ['• « Prix magasin » est obligatoire. « Prix livraison », « Stock initial » et « Prix d\'achat » sont facultatifs.'],
        ['• Les produits déjà existants (même nom + catégorie) sont ignorés (rien n\'est écrasé).'],
        [],
        ['EXEMPLE'],
        ENTETES,
        ['Téléphone', 'Smartphone X', 'Modèle 2024', '128 Go', 'Noir', '2500000', '2550000', '5', '2100000'],
        ['Téléphone', 'Smartphone X', 'Modèle 2024', '128 Go', 'Blanc', '2500000', '2550000', '3', '2100000'],
        ['Fer à béton', 'Barre HA', '', '10 mm', '', '85000', '90000', '200', '70000'],
        ['Ciment', 'Sac 50 kg', '', '', '', '95000', '98000', '40', '82000'],
        [],
        ['VOS CATÉGORIES ET LEURS VARIATIONS'],
        ['Catégorie', 'Variation 1', 'Variation 2'],
      ];
      if (categories.length === 0) {
        info.push(['(Aucune catégorie. Créez-les d\'abord dans Paramètres → Catalogues.)', '', '']);
      } else {
        categories.forEach(c => {
          const k = caracsDe(c);
          info.push([
            c.nom,
            k[0] || '(produit simple — laisser vide)',
            k[1] || '(laisser vide)',
          ]);
        });
      }
      const wsI = XLSX.utils.aoa_to_sheet(info);
      wsI['!cols'] = [{ wch: 32 }, { wch: 26 }, { wch: 26 }];
      XLSX.utils.book_append_sheet(wb, wsI, 'Instructions');

      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buf], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'Modele_Produits_Gestion_Commerce.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Impossible de générer le modèle');
    }
  };

  // ── Lecture du fichier rempli ──────────────────────────────────────────────
  const onFichier = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNomFichier(file.name);
    try {
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: 'array' });
      const nom = wb.SheetNames.find(n => n.toLowerCase().startsWith('produit'))
        || wb.SheetNames.find(n => n.toLowerCase() !== 'instructions')
        || wb.SheetNames[0];
      const ws = wb.Sheets[nom];
      const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' });
      const corps = aoa.slice(1); // on enlève la ligne d'en-têtes
      const out = [];
      corps.forEach((r, i) => {
        const cell = (j) => String(r[j] ?? '').trim();
        const ligne = {
          _ligne: i + 2,
          categorie: cell(0), nom: cell(1), description: cell(2),
          variation1: cell(3), variation2: cell(4),
          prix_magasin: cell(5), prix_livraison: cell(6),
          stock_initial: cell(7), prix_achat: cell(8),
        };
        // ignore les lignes totalement vides
        if (!ligne.categorie && !ligne.nom && !ligne.prix_magasin) return;
        out.push(ligne);
      });
      if (out.length === 0) {
        toast.error('Aucune ligne trouvée dans la feuille « Produits ».');
        return;
      }
      setLignes(out);
      setEtape('apercu');
    } catch (err) {
      toast.error('Fichier illisible. Utilisez le modèle fourni (.xlsx).');
    } finally {
      e.target.value = '';
    }
  };

  const ligneIncomplete = (l) => !l.categorie || !l.nom || !l.prix_magasin;
  const nbIncompletes = lignes.filter(ligneIncomplete).length;

  // ── Envoi au backend ───────────────────────────────────────────────────────
  const importer = async () => {
    setChargement(true);
    try {
      const { data } = await api.post('/produits/import', { lignes });
      setRapport(data.resume ? data : { resume: data, erreurs: [] });
      setEtape('rapport');
      if (data?.resume?.produits_crees > 0) {
        toast.success(`${data.resume.produits_crees} produit(s) importé(s)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Échec de l\'import');
    } finally {
      setChargement(false);
    }
  };

  const terminer = () => { onSuccess && onSuccess(); };

  // ── Rendus ─────────────────────────────────────────────────────────────────
  if (etape === 'rapport' && rapport) {
    const r = rapport.resume || {};
    const errs = rapport.erreurs || [];
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            ['Produits créés', r.produits_crees, 'text-green-400'],
            ['Variantes créées', r.variantes_creees, 'text-green-400'],
            ['Ignorés (déjà là)', r.ignores, 'text-yellow-400'],
            ['Lignes en erreur', r.erreurs, errs.some(e => e.type !== 'info') ? 'text-red-400' : 'text-gray-300'],
          ].map(([lab, val, col]) => (
            <div key={lab} className="bg-gray-800 border border-gray-700 rounded-xl p-3 text-center">
              <div className={`text-2xl font-bold ${col}`}>{val ?? 0}</div>
              <div className="text-xs text-gray-400 mt-1">{lab}</div>
            </div>
          ))}
        </div>

        {errs.length > 0 && (
          <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-3 max-h-64 overflow-y-auto">
            <p className="text-sm font-semibold text-gray-200 mb-2">Détails ({errs.length})</p>
            <ul className="space-y-1.5">
              {errs.map((e, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  {e.type === 'info'
                    ? <Info size={15} className="text-yellow-400 shrink-0 mt-0.5" />
                    : <AlertTriangle size={15} className="text-red-400 shrink-0 mt-0.5" />}
                  <span className="text-gray-300">
                    <span className="text-gray-500">Ligne {e.ligne} : </span>{e.motif}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={() => { setEtape('accueil'); setLignes([]); setRapport(null); setNomFichier(''); }}
            className="px-4 py-2 rounded-lg text-sm bg-gray-700 hover:bg-gray-600 text-gray-200">
            Importer un autre fichier
          </button>
          <button onClick={terminer}
            className="px-4 py-2 rounded-lg text-sm bg-yellow-600 hover:bg-yellow-500 text-black font-medium">
            Terminer
          </button>
        </div>
      </div>
    );
  }

  if (etape === 'apercu') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <button onClick={() => { setEtape('accueil'); setLignes([]); }}
            className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white">
            <ArrowLeft size={15} /> Retour
          </button>
          <span className="text-sm text-gray-400 inline-flex items-center gap-2">
            <FileSpreadsheet size={15} className="text-green-500" /> {nomFichier}
          </span>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-xl p-3 text-sm text-gray-300">
          <b className="text-white">{lignes.length}</b> ligne(s) détectée(s).
          {nbIncompletes > 0 && (
            <span className="text-yellow-400"> {nbIncompletes} ligne(s) à vérifier (catégorie, nom ou prix manquant) — elles seront signalées à l'import.</span>
          )}
        </div>

        <div className="overflow-x-auto border border-gray-700 rounded-xl max-h-72 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-800 text-gray-300 sticky top-0">
              <tr>
                {['#', 'Catégorie', 'Produit', 'Var. 1', 'Var. 2', 'Prix mag.', 'Stock'].map(h => (
                  <th key={h} className="text-left px-3 py-2 font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lignes.slice(0, 50).map((l, i) => (
                <tr key={i} className={`border-t border-gray-800 ${ligneIncomplete(l) ? 'bg-red-500/5' : ''}`}>
                  <td className="px-3 py-1.5 text-gray-500">{l._ligne}</td>
                  <td className="px-3 py-1.5 text-gray-300">{l.categorie || <span className="text-red-400">—</span>}</td>
                  <td className="px-3 py-1.5 text-white">{l.nom || <span className="text-red-400">—</span>}</td>
                  <td className="px-3 py-1.5 text-gray-400">{l.variation1}</td>
                  <td className="px-3 py-1.5 text-gray-400">{l.variation2}</td>
                  <td className="px-3 py-1.5 text-gray-300">{l.prix_magasin || <span className="text-red-400">—</span>}</td>
                  <td className="px-3 py-1.5 text-gray-400">{l.stock_initial}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {lignes.length > 50 && <p className="text-xs text-gray-500">Aperçu des 50 premières lignes — toutes seront importées.</p>}

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm bg-gray-700 hover:bg-gray-600 text-gray-200">
            Annuler
          </button>
          <button onClick={importer} disabled={chargement}
            className="px-4 py-2 rounded-lg text-sm bg-yellow-600 hover:bg-yellow-500 text-black font-medium inline-flex items-center gap-2 disabled:opacity-60">
            <Upload size={15} /> {chargement ? 'Import en cours…' : `Importer ${lignes.length} ligne(s)`}
          </button>
        </div>
      </div>
    );
  }

  // étape « accueil »
  return (
    <div className="space-y-5">
      <ol className="space-y-3">
        <li className="flex gap-3">
          <span className="shrink-0 w-7 h-7 rounded-full bg-yellow-600 text-black font-bold text-sm flex items-center justify-center">1</span>
          <div className="flex-1">
            <p className="text-white font-medium">Téléchargez le modèle Excel</p>
            <p className="text-gray-400 text-sm">Adapté à vos catégories. La feuille « Instructions » explique tout, avec des exemples.</p>
            <button onClick={telechargerModele}
              className="mt-2 inline-flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm">
              <Download size={16} /> Télécharger le modèle
            </button>
          </div>
        </li>
        <li className="flex gap-3">
          <span className="shrink-0 w-7 h-7 rounded-full bg-yellow-600 text-black font-bold text-sm flex items-center justify-center">2</span>
          <div className="flex-1">
            <p className="text-white font-medium">Remplissez-le</p>
            <p className="text-gray-400 text-sm">Une ligne par variante. Répétez la catégorie et le nom pour décliner un produit.</p>
          </div>
        </li>
        <li className="flex gap-3">
          <span className="shrink-0 w-7 h-7 rounded-full bg-yellow-600 text-black font-bold text-sm flex items-center justify-center">3</span>
          <div className="flex-1">
            <p className="text-white font-medium">Importez le fichier rempli</p>
            <p className="text-gray-400 text-sm">Vous verrez un aperçu, puis un rapport (créés, ignorés, erreurs).</p>
            <label className="mt-2 inline-flex items-center gap-2 bg-yellow-600 hover:bg-yellow-500 text-black px-4 py-2 rounded-lg text-sm font-medium cursor-pointer">
              <Upload size={16} /> Choisir le fichier (.xlsx)
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={onFichier} />
            </label>
          </div>
        </li>
      </ol>

      <div className="flex items-start gap-2 bg-blue-500/10 border border-blue-500/30 rounded-xl p-3">
        <CheckCircle2 size={16} className="text-blue-400 shrink-0 mt-0.5" />
        <p className="text-sm text-gray-300">
          L'import <b>crée uniquement de nouveaux produits</b>. Les produits déjà existants (même nom et catégorie)
          sont ignorés — rien n'est écrasé. La création manuelle reste disponible à tout moment.
        </p>
      </div>
    </div>
  );
}
